import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Card,
  Link,
  Collapse,
  CircularProgress,
  IconButton,
} from "@mui/material";
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { getBrandsLocationsData, getStoreLocationsData } from "../../../api/Product";
import { useVehicleReservation } from "../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import SelectBoxGrid from "../../../components/SelectBoxGrid";
import { useStoreDetails } from "../../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import RentalHeader from "../RentalHeader";
import FloatingActionButton from "../../FloatingActionButton";
import { useStepper } from "../../../common/Providers/StepperProvider/useStepper";
import FormSelect from "../../ui/FormSelect";
import FormAutocomplete from "../../ui/FormAutocomplete";
import { searchAddress, getPlantationsData, searchUspsAddress, retrieveUspsAddress } from "../../../api/Store";
import { useCustomSnackbar } from "../../../common/Providers/SnackbarProvider/CustomSnackbarProvider";
import { useManageKeys } from "../../../common/Providers/ManageKeysProvider/UseManageKeys";

const buildManualAddress = (base: string, unit: string) => {
  const trimmed = unit.trim();
  if (!trimmed) return base;
  return `${base}, Unit ${trimmed}`;
};

const PreferencesStep: React.FC = () => {
  const { next, back, isFirstStep } = useStepper();
  const { VehicleReservationMain, setVehicleReservationValue } = useVehicleReservation();
  const { storeDetails, storeContent } = useStoreDetails();
  const { snackbarWithStyle } = useCustomSnackbar();
  const { getKey } = useManageKeys();

  const DATA = {
    subAreas: [
      { id: "downtown", label: "Downtown" },
      { id: "local", label: "Other Local Area" },
    ],
    types: [
      { id: "ride_off", label: new URLSearchParams(window.location.search).get('partner_brand') ? 'Pick up from partner' : getKey('pick_up_from_a_hotel', 'Pickup') },
      { id: "delivery", label: getKey('deliver_to_my_address', 'Delivery') },
    ],
  };

  const [brandsLocations, setBrandsLocations] = useState<any>([]);
  const [plantations, setPlantations] = useState<any>([]);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { mainArea, subArea, orderType, hotel, deliveryAddr } = VehicleReservationMain;

  const isRentalFlow = storeDetails.isRentalFlow;

  // Auto-set subArea when mainArea is set (sub-area selector is hidden)
  useEffect(() => {
    if (mainArea && !subArea) {
      setVehicleReservationValue("subArea", "downtown");
    }
  }, [mainArea]);

  // Auto-set orderType from URL param (e.g. order_type=delivery from partner redirect)
  useEffect(() => {
    const orderTypeParam = new URLSearchParams(window.location.search).get('order_type');
    if (orderTypeParam && !orderType) {
      setVehicleReservationValue("orderType", orderTypeParam);
    }
  }, []);

  const [isCapturingLocation, setIsCapturingLocation] = useState(false);

  const captureLocation = async (): Promise<{ latitude: number; longitude: number; address: string } | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        snackbarWithStyle('Geolocation is not supported by your browser', 'error');
        resolve(null);
        return;
      }

      setIsCapturingLocation(true);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            // Reverse geocode using Nominatim
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
              {
                headers: {
                  'User-Agent': 'BodhiRentals/1.0'
                }
              }
            );

            const data = await response.json();
            const addr = data.address || {};

            // Build a clean, accurate address string
            const houseNumber = addr.house_number || '';
            const road = addr.road || addr.pedestrian || addr.path || '';
            const suburb = addr.suburb || addr.neighbourhood || addr.village || '';
            const city = addr.city || addr.town || addr.municipality || '';
            const state = addr.state || '';
            const postcode = addr.postcode || '';

            let addressParts = [];
            if (houseNumber && road) addressParts.push(`${houseNumber} ${road}`);
            else if (road) addressParts.push(road);

            if (suburb) addressParts.push(suburb);
            if (city) addressParts.push(city);
            if (state) addressParts.push(state);
            if (postcode) addressParts.push(postcode);

            const address = addressParts.length > 0
              ? addressParts.join(', ')
              : data.display_name || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

            const locationInfo = { latitude, longitude, address };

            setVehicleReservationValue("deliveryAddr", address);
            setVehicleReservationValue("manual_address", address);
            setVehicleReservationValue("use_manual", true);

            setIsCapturingLocation(false);
            snackbarWithStyle('Location detected successfully', 'success');
            resolve(locationInfo);
          } catch (error) {
            console.error('Error reverse geocoding:', error);
            const address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            const locationInfo = { latitude, longitude, address };

            setVehicleReservationValue("deliveryAddr", address);
            setVehicleReservationValue("manual_address", address);
            setVehicleReservationValue("use_manual", true);

            setIsCapturingLocation(false);
            snackbarWithStyle('Location detected (using coordinates)', 'success');
            resolve(locationInfo);
          }
        },
        (error) => {
          setIsCapturingLocation(false);

          if (error.code === error.PERMISSION_DENIED) {
            snackbarWithStyle('Location permission denied. Please enable location access.', 'error');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            snackbarWithStyle('Location information unavailable', 'error');
          } else if (error.code === error.TIMEOUT) {
            snackbarWithStyle('Location request timed out', 'error');
          } else {
            snackbarWithStyle('Failed to get location', 'error');
          }

          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    });
  };

  useEffect(() => {
    let active = true;
    if (storeDetails.brand_id) {
      getStoreLocationsData({ brand_id: storeDetails.brand_id, includeBrands: true }, (jsonRes: any, status) => {
        if (active) {
          if (status === 200) {
            setBrandsLocations(jsonRes || []);
          } else {
            setBrandsLocations([]);
          }
        }
      });
    }
    return () => { active = false; };
  }, [storeDetails.brand_id]);

  useEffect(() => {
    let active = true;
    getPlantationsData(storeDetails.store_belong_to, (jsonRes: any, status) => {
      if (active) {
        if (status === 200 && Array.isArray(jsonRes)) {
          setPlantations(jsonRes);
        } else {
          setPlantations([]);
        }
      }
    });
    return () => { active = false; };
  }, [storeDetails.store_belong_to]);

  useEffect(() => {
    if (!storeDetails.brand_id || brandsLocations.length === 0) return;
    if (VehicleReservationMain.mainArea) return; // already set

    // Find this brand's location via locationStores
    const matchingLocation = brandsLocations.find((location: any) =>
      location.locationStores?.some((store: any) =>
        store.brands?.id === storeDetails.brand_id
      )
    );

    // Fallback: use store_belong_to (e.g. 'charleston', 'hiltonhead') when no locationStores match found
    const locationGroup = matchingLocation?.location_group || storeDetails.store_belong_to || '';
    if (locationGroup) {
      setVehicleReservationValue("mainArea", locationGroup);
    }

    if (!matchingLocation) return;

    // For partner sites with pickup — also pre-set hotel and ride_off
    if (storeDetails.brands?.is_partner_site && storeDetails.brands?.offer_pickup) {
      setVehicleReservationValue("subArea", 'downtown');
      if (!VehicleReservationMain.orderType) {
        setVehicleReservationValue("orderType", 'ride_off');
      }
      setVehicleReservationValue("use_manual", true);
      setVehicleReservationValue("hotel", matchingLocation.id);

      const fullAddress = [
        matchingLocation.address_line1,
        matchingLocation.address_line2,
        matchingLocation.city,
        matchingLocation.state,
        matchingLocation.postal_code
      ].filter(Boolean).join(', ');
      setVehicleReservationValue("manual_address", fullAddress || matchingLocation.location || "");
    }
  }, [brandsLocations]);

  const [searchKey, setSearchKey] = useState<string>("");
  const [searchedAddresses, setSearchedAddresses] = useState<Array<any>>([]);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // USPS Address Lock-in states
  const requireUsps = !!storeDetails.require_usps_address;
  const [uspsSearchKey, setUspsSearchKey] = useState<string>("");
  const [uspsSuggestions, setUspsSuggestions] = useState<Array<any>>([]);
  const [addressUnit, setAddressUnit] = useState<string>("");

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchKey) {
      searchTimeoutRef.current = setTimeout(() => {
        searchAddress(searchKey, storeDetails.id, (jsonRes: any, status) => {
          if (status === 200 && Array.isArray(jsonRes)) {
            let filteredAddresses = jsonRes.map((address, index) => {
              const label = getAddressLabel(address, index);
              if (label === '') return null;
              return { ...address, label: label };
            });

            filteredAddresses = filteredAddresses.filter((item) => item);
            setSearchedAddresses(filteredAddresses);
          } else {
            setSearchedAddresses([]);
          }
        });
      }, 300);
    } else {
      setSearchedAddresses([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchKey, storeDetails.id]);

  // USPS Address Lock-in: live Mapbox-backed typeahead for verified US addresses.
  // Replaces the internal address library when the brand has require_usps_address enabled.
  useEffect(() => {
    if (!requireUsps || orderType !== 'delivery') {
      setUspsSuggestions([]);
      return;
    }
    if (!uspsSearchKey) {
      setUspsSuggestions([]);
      return;
    }
    const handle = setTimeout(() => {
      searchUspsAddress(uspsSearchKey, (jsonRes: any, status: any) => {
        if (status === 200 && Array.isArray(jsonRes)) {
          setUspsSuggestions(
            jsonRes.map((address: any, index: number) => ({
              ...address,
              label: address.full_address || ''
            }))
          );
        } else {
          setUspsSuggestions([]);
        }
      }, storeDetails.usps_zip_codes || undefined);
    }, 300);
    return () => clearTimeout(handle);
  }, [uspsSearchKey, requireUsps, orderType, storeDetails.usps_zip_codes]);

  const getAddressLabel = (address: any, index: number) => {
    if (!address.property_type && address.property_type !== 0) {
      return '';
    }

    switch (address.property_type.toString()) {
      case '0':
        return `${address.number || ''} ${address.street || ''}, ${address.plantation || ''}${address.property_name ? ` - ${address.property_name}` : ''}`;
      case '1':
        return `${address.number || ''} ${address.street === '' || address.street === null ? address.property_name || '' : address.street || ''}, ${address.plantation || ''}`;
      default:
        return '';
    }
  };

const filteredLocations = useMemo(() => {
  return brandsLocations.filter((item: any) => {
    // 1. Filter by area group if one is locked in
    if (mainArea && item.location_group !== mainArea) return false;
    
    // 2. Drive-off filter for pickup orders
    if (orderType === "ride_off" && !item.is_driveoff_location) return false;
    
    return true;
  });
}, [brandsLocations, mainArea, orderType]);

  const shouldShowSubArea = false;
  const shouldShowOrderType = !!mainArea;

  const allStepsCompleted =
    orderType &&
    ((orderType === "ride_off" && hotel) ||
      (orderType === "delivery" && 
        (requireUsps
          ? !!VehicleReservationMain.mapbox_id && VehicleReservationMain.manual_address?.trim().length > 0
          : VehicleReservationMain.plantation_id && VehicleReservationMain.manual_address?.trim().length > 0
        )
      ));

  const onComplete = () => next();

  const orderTypeChangeHandler = (type: string) => {
    setVehicleReservationValue("orderType", type);

    if (type === "delivery") {
      setVehicleReservationValue("use_manual", true);
      setVehicleReservationValue("address_id", null);
      setVehicleReservationValue("manual_address", "");
      setVehicleReservationValue("usps_address", "");
      setVehicleReservationValue("mapbox_id", null);
      setVehicleReservationValue("lat", null);
      setVehicleReservationValue("lng", null);
      setVehicleReservationValue("plantation_id", null);
      setUspsSearchKey("");
      setUspsSuggestions([]);
      setAddressUnit("");
    }

    // If on a partner redirect and user picks delivery, redirect to the delivery-only brand
    const queryParams = new URLSearchParams(window.location.search);
    const partnerBrandId = queryParams.get('partner_brand');
    const partnerLocationId = queryParams.get('partner_location');

    const isPartnerSite = storeDetails.brands?.is_partner_site === true;
    const shouldRedirectForDelivery = type === "delivery" && (partnerBrandId || isPartnerSite);

    if (shouldRedirectForDelivery) {
      setIsRedirecting(true);
      getStoreLocationsData({ includeBrands: true }, (jsonRes: any, status: any) => {
        if (status !== 200 || !Array.isArray(jsonRes)) { setIsRedirecting(false); return; }

        // Find this brand's location first (by brand_id via locationStores)
        const myLocation = jsonRes.find((l: any) =>
          l.locationStores?.some((ls: any) => ls.brands?.id === storeDetails.brand_id)
        );
        const deliveryLocationId: string = partnerLocationId || (myLocation ? String(myLocation.id) : '');

        // Find a delivery-only store (is_partner_site=false, offer_pickup=false) — store_url may be absent, fallback used below
        let deliveryStore: any = null;
        for (const loc of jsonRes) {
          const found = loc.locationStores?.find(
            (ls: any) => !ls.brands?.is_partner_site && !ls.brands?.offer_pickup
          );
          if (found) { deliveryStore = found; break; }
        }

        const storeUrl = deliveryStore?.brands?.store_url || 'carolinarides.hosted.bodhisys.io';
        if (!storeUrl) return;
        const params = new URLSearchParams({
          partner_location: deliveryLocationId,
          partner_brand: partnerBrandId ?? String(storeDetails.brand_id),
          order_type: 'delivery',
        });

        const protocol = storeUrl.startsWith('localhost') ? 'http' : 'https';
        window.location.href = `${protocol}://${storeUrl}/rental?${params.toString()}`;
      });
      return;
    }

    if (!storeDetails?.brands?.is_partner_site) {
      switch (type) {
        case "delivery":
          setVehicleReservationValue("manual_address", "");
          break;
        case "ride_off":
          const addr = filteredLocations.find((item: any) => item.id === hotel);
          setVehicleReservationValue("manual_address", addr?.location || "");
          break;
        default:
          break;
      }
    }
  };

  const locationChangeHandler = (value: any, selected: any) => {
    setVehicleReservationValue("hotel", value);
    setVehicleReservationValue("use_manual", true);
    setVehicleReservationValue("manual_address", selected?.fullAddress || "");
  };

  const addressInputChangeHandler = (event: any, value: string) => {
    // Ignore events from the hidden autocomplete when in manual mode
    if (VehicleReservationMain.use_manual) return;
    setSearchKey(value);
    setVehicleReservationValue("deliveryAddr", value);
    // Typing (not selecting) clears the selected address
    setVehicleReservationValue("selectedAddress", null);
    setVehicleReservationValue("address_id", null);
    setVehicleReservationValue("manual_address", "");
  };

  const addressChangeHandler = (event: any, value: any) => {
    if (value && typeof value !== 'string') {
      const labelWithoutHtml = value.label.replace(/<[^>]*>?/gm, '');
      setVehicleReservationValue("deliveryAddr", labelWithoutHtml);
      setVehicleReservationValue("selectedAddress", value);
      setVehicleReservationValue("address_id", value.id);
      setVehicleReservationValue("use_manual", false);
      setVehicleReservationValue("manual_address", labelWithoutHtml);
      // Save plantation data from the selected address
      setVehicleReservationValue("plantation", value.plantation || null);
      setVehicleReservationValue("plantation_id", value.plantation_id || null);
    }
  };

  const manualAddressChangeHandler = (e: any) => {
    setVehicleReservationValue("manual_address", e.target.value);
    setVehicleReservationValue("deliveryAddr", e.target.value);
  };

  const handleUspsSelect = async (value: any) => {
    if (!value) {
      setVehicleReservationValue("selectedAddress", null);
      setVehicleReservationValue("usps_address", "");
      setVehicleReservationValue("mapbox_id", null);
      setVehicleReservationValue("manual_address", "");
      setVehicleReservationValue("deliveryAddr", "");
      setVehicleReservationValue("lat", null);
      setVehicleReservationValue("lng", null);
      setAddressUnit("");
      return;
    }
    const base = value.full_address || "";
    setVehicleReservationValue("selectedAddress", value);
    setVehicleReservationValue("usps_address", base);
    setVehicleReservationValue("mapbox_id", value.mapbox_id || null);
    setVehicleReservationValue("manual_address", buildManualAddress(base, addressUnit));
    setVehicleReservationValue("deliveryAddr", buildManualAddress(base, addressUnit));
    if (value.mapbox_id) {
      try {
        const res: any = await retrieveUspsAddress(value.mapbox_id);
        if (res && res.ok) {
          const data = await res.clone().json();
          if (data && data.mapbox_id) {
            setVehicleReservationValue("lat", data.latitude ?? null);
            setVehicleReservationValue("lng", data.longitude ?? null);
          }
        }
      } catch (err) {
        console.error('[PreferencesStep] retrieveUspsAddress failed:', err);
      }
    }
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressUnit(value);
    if (VehicleReservationMain.usps_address) {
      const full = buildManualAddress(VehicleReservationMain.usps_address, value);
      setVehicleReservationValue("manual_address", full);
      setVehicleReservationValue("deliveryAddr", full);
    }
  };

  if (isRedirecting) {
    return (
      <Box sx={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.95)', zIndex: 9999 }}>
        <CircularProgress size={48} sx={{ color: storeContent.primary_color || '#0047C0', mb: 2 }} />
        <Typography variant="body1" fontWeight={600}>Redirecting, please wait...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <RentalHeader title={storeDetails.store_name} onActionClick={() => back()} showActionButton={!isFirstStep} primaryColor={storeContent.primary_color} />
      <Box sx={styles.contentContainer}>
        <Typography variant="h1" sx={styles.contentTitle}>
          {getKey('place_order', 'Place Order')}
        </Typography>

        {/* Step 1: Sub Area (only if mainArea === "charleston") */}
        {!isRentalFlow && shouldShowSubArea && !storeDetails.brands?.is_partner_site && (
          <SelectBoxGrid
            title=""
            options={DATA.subAreas}
            selectedId={subArea}
            onSelect={(area) => {
              setVehicleReservationValue("subArea", area);
            }}
          />
        )}

        {/* Step 2: Order Type */}
        {!isRentalFlow && shouldShowOrderType && (
          <SelectBoxGrid
            title={getKey('how_would_you_like_to_receive_your_rental', 'How would you like to receive your rental?')}
            options={DATA.types}
            selectedId={orderType}
            onSelect={orderTypeChangeHandler}
          />
        )}

        {/* Step 3A: Ride-off => Dropdown */}
        {orderType === "ride_off" && (
          <Box sx={{ p: 1.5, mb: 2 }}>
            <Typography fontWeight={600} mb={1}>
              Pickup Location
            </Typography>
            <FormSelect
              label="Select Location"
              value={hotel}
              options={filteredLocations.map((item: any) => ({
                value: item.id,
                name: item.name,
                label: item.location,
                fullAddress: item.location
              }))}
              getOptionValue={(opt) => opt.value}
              getOptionLabel={(opt) => `${opt?.name ? opt.name + " - " : ""}${opt.fullAddress}` || "No address available"}
              onChange={locationChangeHandler}
              primaryColor={storeContent.primary_color}
              disabled={storeDetails.brands?.is_partner_site === true}
            />
          </Box>

        )}

        {/* Step 3B: Delivery => Address Field */}
        {orderType === "delivery" && (
          <Box sx={{ p: 1.5, mb: 2 }}>
            {!requireUsps && (
              <>
                <Typography fontWeight={600} mb={1}>
                  Select Area
                </Typography>
                <FormSelect
                  label="Select Area"
                  value={VehicleReservationMain.plantation_id || ""}
                  options={plantations.map((item: any) => ({
                    value: item.id,
                    name: item.plantation,
                    label: item.plantation
                  }))}
                  getOptionValue={(opt) => opt.value}
                  getOptionLabel={(opt) => opt.label || ""}
                  onChange={(value: any, selected: any) => {
                    setVehicleReservationValue("plantation_id", value ? Number(value) : null);
                    setVehicleReservationValue("plantation", selected?.name || "");
                  }}
                  primaryColor={storeContent.primary_color}
                />
              </>
            )}

            {requireUsps ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1 }}>
                  <Typography fontWeight={600}>
                    Verified Delivery Address
                  </Typography>
                </Box>
                <FormAutocomplete
                  label="Search and select a verified address"
                  placeholder="Start typing your address..."
                  value={VehicleReservationMain.manual_address || uspsSearchKey}
                  options={uspsSuggestions}
                  disableClearable
                  getOptionLabel={(opt: any) => typeof opt === 'string' ? opt : opt.label}
                  onInputChange={(event: any, value: string) => {
                    setUspsSearchKey(value);
                    if (!value) {
                      handleUspsSelect(null);
                    }
                  }}
                  onChange={(event: any, value: any) => {
                    handleUspsSelect(value);
                  }}
                  primaryColor={storeContent.primary_color}
                />
                {VehicleReservationMain.selectedAddress && (
                  <TextField
                    label="Apt / Unit / Condo # (optional)"
                    placeholder="e.g. Villa 1201"
                    value={addressUnit}
                    onChange={handleUnitChange}
                    fullWidth
                    size="small"
                    sx={{ mt: 2 }}
                  />
                )}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    This brand requires a verified USPS address. Please select a suggestion from the list.
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography fontWeight={600}>
                    {VehicleReservationMain.use_manual ? "Enter Address " : "Enter Delivery Address"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={captureLocation}
                    disabled={isCapturingLocation}
                    sx={{
                      color: storeContent.primary_color || "#0047C0",
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    {isCapturingLocation ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <MyLocationIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <Collapse in={!VehicleReservationMain.use_manual}>
                  <FormAutocomplete
                    label="Select or Type Address"
                    placeholder="Search for your address..."
                    value={deliveryAddr}
                    options={searchedAddresses}
                    disableClearable
                    getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
                    onInputChange={addressInputChangeHandler}
                    onChange={addressChangeHandler}
                    primaryColor={storeContent.primary_color}
                  />
                </Collapse>

                <Collapse in={VehicleReservationMain.use_manual}>
                  <Box>
                    <Card
                      sx={{
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: storeContent.primary_color || "#0047C0",
                        backgroundColor: "background.paper",
                      }}
                    >
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="Resort, Street Address, Apt #/Suite/Etc."
                        value={VehicleReservationMain.manual_address || ""}
                        onChange={manualAddressChangeHandler}
                      />
                    </Card>
                  </Box>
                </Collapse>

                <Box sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      const switchingToManual = !VehicleReservationMain.use_manual;
                      if (switchingToManual) {
                        // Switching to manual: clear search state, set use_manual = true (1)
                        setVehicleReservationValue("use_manual", true);
                        setVehicleReservationValue("selectedAddress", null);
                        setVehicleReservationValue("address_id", null);
                        setVehicleReservationValue("deliveryAddr", "");
                        setSearchKey("");
                      } else {
                        // Switching to search: clear manual state, set use_manual = false (0)
                        setVehicleReservationValue("use_manual", false);
                        setVehicleReservationValue("manual_address", "");
                        setVehicleReservationValue("deliveryAddr", "");
                        setSearchKey("");
                      }
                    }}
                    sx={{ cursor: "pointer", textDecoration: "none", color: storeContent.primary_color || "#0047C0" }}
                  >
                    {VehicleReservationMain.use_manual ? "Search for address instead?" : "Address not listed? Manually enter address."}
                  </Link>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Floating Continue Button */}
        <FloatingActionButton label="Continue" primaryColor={storeContent.primary_color} onClick={onComplete} visible={!!allStepsCompleted} disabled={!allStepsCompleted} />
      </Box>
    </Box>
  );
};

const styles = {
  contentContainer: {
    flex: 1,
    p: 1.5,
    backgroundColor: "white",
    borderBottomLeftRadius: "4px",
    borderBottomRightRadius: "4px",
  },
  contentTitle: {
    fontSize: "24px",
    fontWeight: 600,
    mb: 2,
  },
};

export default PreferencesStep;
